import { useState } from 'react'

import { useQuery } from '@apollo/client'
import { useParams } from 'react-router-dom'

import {
  ExternalPaymentDocument,
  ExternalPaymentRecordsDocument,
} from '__generated__/atlas-types'

import { Flex } from 'styles/components'
import { fromGlobalId, toGlobalId } from 'utils/transformations/graphql'

import { atlasClient } from 'graphql/client'

import { Grid, Loader } from 'components/Core'

import { ContainerRow } from '../../PublicInvoice/PublicInvoice.ui'
import Payment from '../../PublicInvoice/components/Payment'
import TransactionHistory from '../../PublicInvoice/components/TransactionHistory'
import { getCurrencyByCountry } from '../../PublicInvoice/components/helpers'
import { useGetLocalCurrenciesFF } from '../../Review/components/PayView/components/Invoice/invoice.hooks'
import { WarningBanner } from '../../components/WarningBanner/WarningBanner'
import { BillingAddressDictionary } from '../../types'
import BillingInfo from './BillingInfo'
import BrandContact from './BrandContact'
import FinancialInfo from './FinancialInfo'
import {
  PageHeaderContainer,
  PageHeaderInfo,
  PageHeaderTitle,
  PaymentsContainer,
  WarningBannerWrapper,
} from './Payments.style'
import { getTotalExternalPayment } from 'features/Orders/utils/helpers'

const POLL_EXTERNAL_PAYMENT_RECORDS_INTERVAL_MS = 1000 * 5

export const useExternalPaymentAmount = (externalPayments?: {
  id: string
  amount: number
}) => {
  const { data: paymentsData, loading: paymentRecordsLoading } = useQuery(
    ExternalPaymentRecordsDocument,
    {
      client: atlasClient,
      variables: {
        externalPaymentId: externalPayments?.id ?? '',
      },
      skip: !externalPayments?.id,
      pollInterval: POLL_EXTERNAL_PAYMENT_RECORDS_INTERVAL_MS,
    },
  )
  if (!paymentsData || !externalPayments) {
    return null
  }

  const totalPaymentAmount = getTotalExternalPayment(
    paymentsData.externalPaymentRecords,
  )

  const totalAmount = externalPayments.amount - totalPaymentAmount

  return { data: totalAmount, loading: paymentRecordsLoading }
}

const Payments = () => {
  const { externalPaymentUUID } = useParams<{ externalPaymentUUID?: string }>()
  const [forceRerenderDate, setForceRerenderDate] = useState(new Date())
  const [canConvertCurrency, setCanConvertCurrency] = useState(false)

  const { data, loading } = useQuery(ExternalPaymentDocument, {
    client: atlasClient,
    variables: {
      uuid: externalPaymentUUID!,
    },
    skip: !externalPaymentUUID,
  })
  const deletedAt = data?.externalPaymentByLink?.deletedAt

  const externalPaymentData = useExternalPaymentAmount(
    data?.externalPaymentByLink,
  )

  const { data: shouldUseLocalCurrencies, loading: localCurrenciesFFLoading } =
    useGetLocalCurrenciesFF({
      brandId: data?.externalPaymentByLink.brandId,
      skipWhen:
        getCurrencyByCountry(data?.externalPaymentByLink.billingCountry) ===
        data?.externalPaymentByLink.currency,
    })

  if (
    !data ||
    loading ||
    !externalPaymentData ||
    externalPaymentData?.loading ||
    localCurrenciesFFLoading
  ) {
    return <Loader active useFullScreen />
  }

  const handleCanCovertCurrency = (canConvert: boolean) => {
    setCanConvertCurrency(canConvert)
  }

  const billingAddressDictionary = {
    name: data.externalPaymentByLink.retailerName,
    email: data.externalPaymentByLink.retailerEmails[0],
    street: data.externalPaymentByLink.billingAddress,
    city: data.externalPaymentByLink.billingCity,
    country: data.externalPaymentByLink.billingCountry,
    state: data.externalPaymentByLink.billingState,
    zip: data.externalPaymentByLink.billingPostalCode,
  } as BillingAddressDictionary
  return (
    <PaymentsContainer>
      <PageHeaderContainer>
        <PageHeaderTitle>Brand</PageHeaderTitle>
        <PageHeaderInfo>{data.externalPaymentByLink.brandName}</PageHeaderInfo>
      </PageHeaderContainer>
      <Grid container>
        <Grid.Row>
          <Grid.Col sm={12} md={12} lg={16}>
            {Boolean(deletedAt) && (
              <Grid.Row>
                <Grid.Col textAlign="right">
                  <WarningBannerWrapper>
                    <WarningBanner
                      text="This payment was cancelled"
                      date={deletedAt}
                    />
                  </WarningBannerWrapper>
                </Grid.Col>
              </Grid.Row>
            )}
            <ContainerRow>
              <Grid.Col sm={12} md={6} lg={8}>
                <Flex flexDirection="column" rowGap="20px" alignItems="stretch">
                  <TransactionHistory
                    currencyCode={data.externalPaymentByLink.currency}
                    externalPaymentId={data.externalPaymentByLink.id}
                    shouldUseLocalCurrencies={
                      shouldUseLocalCurrencies && canConvertCurrency
                    }
                    billingCountry={data.externalPaymentByLink.billingCountry}
                  />
                  <Grid.Row>
                    <Grid.Col sm={12} md={3} lg={4}>
                      <Flex
                        flexDirection="column"
                        rowGap="20px"
                        alignItems="stretch"
                      >
                        <BillingInfo
                          externalPayment={data.externalPaymentByLink}
                        />
                        <BrandContact
                          name={data.externalPaymentByLink.brandContactName}
                          email={data.externalPaymentByLink.brandContactEmail}
                        />
                      </Flex>
                    </Grid.Col>
                    <Grid.Col sm={12} md={3} lg={4}>
                      <FinancialInfo
                        externalPayment={data.externalPaymentByLink}
                      />
                    </Grid.Col>
                  </Grid.Row>
                </Flex>
              </Grid.Col>
              <Grid.Col sm={12} md={6} lg={8}>
                <Payment
                  {...(!!data.externalPaymentByLink.retailerId && {
                    retailerId:
                      toGlobalId(
                        'RetailerAccount',
                        fromGlobalId(data.externalPaymentByLink.retailerId).id,
                      ) ?? '',
                  })}
                  externalPayment={data.externalPaymentByLink}
                  externalPaymentTotal={externalPaymentData?.data}
                  setForceRerenderDate={setForceRerenderDate}
                  billingAddressDictionary={billingAddressDictionary}
                  key={`${forceRerenderDate}`}
                  shouldUseLocalCurrencies={shouldUseLocalCurrencies}
                  disablePayment={Boolean(deletedAt)}
                  onRatesFetched={handleCanCovertCurrency}
                />
              </Grid.Col>
            </ContainerRow>
          </Grid.Col>
        </Grid.Row>
      </Grid>
    </PaymentsContainer>
  )
}

export default Payments
